import { useMemo } from "react";
import { useNavigate } from "react-router-dom";

import { AdminButton } from "@/components/admin/button";
import { Card, CardBody, CardHead } from "@/components/admin/card";
import { AdminIcon } from "@/components/admin/icon";
import { PageHead } from "@/components/admin/page-head";
import { Tag } from "@/components/admin/tag";
import { useCodEntries } from "@/hooks/use-payments";
import { fmtFCFA } from "@/lib/format";

const STATUS_CLS: Record<string, "success" | "info" | "warning" | "danger"> = {
	COLLECTED: "info",
	AT_HUB: "warning",
	DEPOSITED: "success",
	RECONCILED: "success",
	DISCREPANCY: "danger",
};

const STATUS_LABEL: Record<string, string> = {
	COLLECTED: "Encaissé livreur",
	AT_HUB: "Au hub",
	DEPOSITED: "Déposé banque",
	RECONCILED: "Rapproché",
	DISCREPANCY: "Écart",
};

export function AdminCodPage() {
	const navigate = useNavigate();
	const { data, isLoading } = useCodEntries();
	const items = useMemo(() => data ?? [], [data]);

	const kpis = useMemo(() => {
		const collected = items
			.filter((i) => i.status === "COLLECTED")
			.reduce((s, i) => s + i.collectedAmount, 0);
		const atHub = items
			.filter((i) => i.status === "AT_HUB")
			.reduce((s, i) => s + i.collectedAmount, 0);
		const discrepancy = items
			.filter((i) => i.status === "DISCREPANCY")
			.reduce((s, i) => s + Math.abs(i.discrepancy), 0);
		return { collected, atHub, discrepancy, count: items.length };
	}, [items]);

	return (
		<>
			<PageHead
				title="Encaissements espèces (COD)"
				sub="Workflow livreur → hub → banque"
				actions={
					<AdminButton variant="ghost" onClick={() => navigate("/paiements")}>
						<AdminIcon n="chevL" s={14} />
						Retour
					</AdminButton>
				}
			/>

			<div className="kpi-grid">
				<div className="kpi success">
					<div className="kpi-l">Encaissé chez livreurs</div>
					<div className="kpi-v tn">{fmtFCFA(kpis.collected)}</div>
				</div>
				<div className="kpi warning">
					<div className="kpi-l">À déposer (hub)</div>
					<div className="kpi-v tn">{fmtFCFA(kpis.atHub)}</div>
				</div>
				<div className="kpi accent">
					<div className="kpi-l">Écarts cumulés</div>
					<div className="kpi-v tn">{fmtFCFA(kpis.discrepancy)}</div>
				</div>
				<div className="kpi">
					<div className="kpi-l">Entrées suivies</div>
					<div className="kpi-v tn">{kpis.count}</div>
				</div>
			</div>

			<Card>
				<CardHead
					title={`Saisies COD (${items.length})`}
					sub="Workflow livreur → hub → banque · alerte écart > 10 k FCFA"
				/>
				{isLoading ? (
					<CardBody>
						<div className="empty">Chargement…</div>
					</CardBody>
				) : items.length === 0 ? (
					<CardBody>
						<div className="empty">Aucune saisie COD enregistrée.</div>
					</CardBody>
				) : (
					<div className="table-wrap" style={{ margin: 0 }}>
						<table className="table">
							<thead>
								<tr>
									<th>Commande</th>
									<th>Livreur</th>
									<th>Hub</th>
									<th className="right">Théorique</th>
									<th className="right">Encaissé</th>
									<th className="right">Écart</th>
									<th>Statut</th>
									<th>Notes</th>
								</tr>
							</thead>
							<tbody>
								{items.map((c) => (
									<tr key={c.id}>
										<td className="mono">{c.orderId?.slice(0, 8) ?? "—"}</td>
										<td className="mono muted">
											{c.driverId?.slice(0, 8) ?? "—"}
										</td>
										<td className="mono muted">
											{c.hubId?.slice(0, 8) ?? "—"}
										</td>
										<td className="right">{fmtFCFA(c.expectedAmount)}</td>
										<td className="right tn">
											<strong>{fmtFCFA(c.collectedAmount)}</strong>
										</td>
										<td
											className="right"
											style={{
												color:
													c.discrepancy !== 0
														? "var(--a-danger, #b23a2a)"
														: undefined,
												fontWeight: c.discrepancy !== 0 ? 600 : undefined,
											}}
										>
											{c.discrepancy !== 0 ? fmtFCFA(c.discrepancy) : "—"}
										</td>
										<td>
											<Tag variant={STATUS_CLS[c.status] ?? "neutral"}>
												{STATUS_LABEL[c.status] ?? c.status}
											</Tag>
										</td>
										<td className="muted" style={{ fontSize: 12 }}>
											{c.notes ?? "—"}
										</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				)}
			</Card>
		</>
	);
}
